/**
 * Short "City 🇦🇹" line for timeline/post cards — no POI/street/country spelled out.
 * Full address stays in tooltip (caller provides title/aria from raw locationName/coords).
 *
 * @see https://operations.osmfoundation.org/policies/nominatim/
 */

export function flagEmojiFromIsoCountryCode(code: string | undefined): string {
  if (!code || code.length !== 2) return '';
  const upper = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return '';
  const BASE = 0x1f1e6;
  return String.fromCodePoint(BASE + upper.charCodeAt(0) - 65, BASE + upper.charCodeAt(1) - 65);
}

const COUNTRY_NAME_TO_ISO: Record<string, string> = {
  österreich: 'AT',
  austria: 'AT',
  deutschland: 'DE',
  germany: 'DE',
  schweiz: 'CH',
  switzerland: 'CH',
  italien: 'IT',
  italy: 'IT',
  frankreich: 'FR',
  france: 'FR',
  spanien: 'ES',
  spain: 'ES',
  polen: 'PL',
  poland: 'PL',
  tschechien: 'CZ',
  'czech republic': 'CZ',
  czechia: 'CZ',
  ungarn: 'HU',
  hungary: 'HU',
  'united kingdom': 'GB',
  'great britain': 'GB',
  großbritannien: 'GB',
  'united states': 'US',
  netherlands: 'NL',
  niederlande: 'NL',
  belgien: 'BE',
  belgium: 'BE',
  portugal: 'PT',
  irland: 'IE',
  ireland: 'IE',
};

function flagFromCountryName(country: string): string {
  const iso = COUNTRY_NAME_TO_ISO[country.trim().toLowerCase()];
  return iso ? flagEmojiFromIsoCountryCode(iso) : '';
}

function isPostcodeSegment(s: string): boolean {
  const t = s.trim();
  if (/^\d{3,10}$/.test(t)) return true;
  if (/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(t)) return true;
  return false;
}

const AT_BUNDESLAND_ONLY = new Set([
  'niederösterreich',
  'oberösterreich',
  'steiermark',
  'kärnten',
  'salzburg',
  'tirol',
  'vorarlberg',
  'burgenland',
]);

function localityIndexFromDisplayName(parts: string[], countryExclusive: number): number {
  let i = countryExclusive - 1;
  while (i >= 0 && isPostcodeSegment(parts[i]!)) {
    i--;
  }
  if (i < 0) return 0;
  for (let j = i; j >= 1; j--) {
    const segment = parts[j]!;
    if (/^bezirk\b/i.test(segment) || /^katastralgemeinde\b/i.test(segment)) {
      return Math.max(0, j - 1);
    }
  }
  let k = i;
  while (k >= 0 && AT_BUNDESLAND_ONLY.has(parts[k]!.trim().toLowerCase())) {
    k--;
  }
  return k >= 0 ? k : i;
}

/** City + optional flag emoji from a long Nominatim-style comma line (no coords). */
export function postsOverviewCityLineFromStoredName(full: string): string {
  const parts = full.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return full;
  if (parts.length === 1) return parts[0]!;
  if (parts.length === 2) {
    const city = parts[0]!;
    const flag = flagFromCountryName(parts[1]!);
    return flag ? `${city} ${flag}` : city;
  }
  const countryName = parts[parts.length - 1]!;
  const city = parts[localityIndexFromDisplayName(parts, parts.length - 1)]!;
  const flag = flagFromCountryName(countryName);
  return flag ? `${city} ${flag}` : city;
}

type NominatimAddress = {
  city?: string;
  town?: string;
  village?: string;
  municipality?: string;
  city_district?: string;
  suburb?: string;
  hamlet?: string;
  county?: string;
  country?: string;
  country_code?: string;
};

function localityFromAddress(address: NominatimAddress): string | undefined {
  return (
    address.city ||
    address.town ||
    address.village ||
    address.municipality ||
    address.city_district ||
    address.suburb ||
    address.hamlet
  );
}

async function reverseFetch(lat: number, lon: number): Promise<string | undefined> {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${encodeURIComponent(String(lat))}&lon=${encodeURIComponent(String(lon))}&zoom=10&addressdetails=1`,
    {
      headers: {
        'User-Agent': 'ITI-Bulletin-Board/1.0',
        Accept: 'application/json',
        'Accept-Language': typeof navigator !== 'undefined' ? navigator.language : 'en',
      },
    }
  );
  if (!response.ok) return undefined;
  const data = (await response.json()) as { address?: NominatimAddress; display_name?: string };
  const addr = data.address;
  const city =
    (addr && localityFromAddress(addr)) ||
    (typeof data.display_name === 'string'
      ? data.display_name.split(',')[0]?.trim()
      : undefined);
  if (!city) return undefined;
  const flag =
    flagEmojiFromIsoCountryCode(addr?.country_code) || flagFromCountryName(addr?.country ?? '');
  return flag ? `${city} ${flag}` : city;
}

const cache = new Map<string, string | undefined>();
const inflight = new Map<string, Promise<string | undefined>>();

function geoKey(lat: number, lon: number): string {
  return `${lat.toFixed(3)}_${lon.toFixed(3)}`;
}

/** Promise resolves to `City 🇦🇹`; cached/deduped. */
export function getPostsOverviewCityLineFromCoordinates(
  lat: number,
  lon: number
): Promise<string | undefined> {
  const key = geoKey(lat, lon);
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  const existing = inflight.get(key);
  if (existing) return existing;
  const p = reverseFetch(lat, lon)
    .then((line) => {
      cache.set(key, line);
      inflight.delete(key);
      return line;
    })
    .catch(() => {
      inflight.delete(key);
      return undefined;
    });
  inflight.set(key, p);
  return p;
}
